import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  CircularProgress,
  Button,
  Stack,
  Card,
  CardContent,
  TextField,
  Grid,
  useMediaQuery,
  useTheme,
  Divider,
  Tooltip,
  Alert,
  Chip,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select
} from '@mui/material';
import { useSnackbar } from 'notistack';
import facturaCompraService, {
  LibroIvaComprasItem
} from '../../services/facturaCompraService';
import { formatCurrency } from '../../utils/formatters';
import {
  Article as ArticleIcon,
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PictureAsPdfIcon,
  ArrowBack as ArrowBackIcon,
  ReceiptLongOutlined as ReceiptLongOutlinedIcon,
  PriceCheck as PriceCheckIcon,
  Percent as PercentIcon,
  AttachMoney as AttachMoneyIcon,
  PlaceOutlined as PlaceOutlinedIcon,
  BadgeOutlined as BadgeOutlinedIcon,
  SortByAlpha as SortByAlphaIcon,
} from '@mui/icons-material';
import KpisBar, { type KpiBarItem } from '../../components/shared/KpisBar';
import {
  LibroIvaResumenAccordion,
  LibroIvaResumenAgrupadoTable,
  libroIvaFooterCellSx as footerCellSx,
  type LibroIvaResumenGrupo
} from '../../components/shared/LibroIvaResumenes';
import {
  claveProvincia,
  provinciaEnFiltro,
  SIN_PROVINCIA_KEY,
  SIN_PROVINCIA_LABEL
} from '../../utils/libroIvaVentasProvincias';
import provinciaService, { Provincia } from '../../services/provinciaService';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import PDFViewer from '../../components/shared/PDFViewer';
import SearchableSelect from '../../components/SearchableSelect';
import companiaService, { Compania } from '../../services/companiaService';
import {
  fetchCompaniaParaPdf,
  fetchLogoPngDataUrl,
  lineasDatosCompania,
  pdfLibroIvaComprasColumnStyles,
  pdfLibroIvaTableWidth,
  pdfFootLabelCell,
  pdfFootAmountCell,
  pdfLibroIvaFootStyles,
  PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_COMPRAS,
  PDF_LIBRO_IVA_COMPRAS_HEAD,
  pdfLibroIvaComprasTableStyles,
  formatCurrencyPdfCompact,
  type PdfFootCellDef
} from '../../utils/pdfLibroIvaCompania';
import { persistGlobalIdCompania, readGlobalIdCompania } from '../../utils/companiaPreferida';

// Función para formatear fecha en formato DD/MM/AAAA con ceros a la izquierda
const formatearFecha = (fecha: Date | string): string => {
  const date = typeof fecha === 'string' ? new Date(fecha) : fecha;
  if (isNaN(date.getTime())) return '';
  
  const dia = date.getDate().toString().padStart(2, '0');
  const mes = (date.getMonth() + 1).toString().padStart(2, '0');
  const año = date.getFullYear();
  
  return `${dia}/${mes}/${año}`;
};

interface LibroIvaComprasRow {
  fechaEmision: string;
  tipoComprobante: string;
  puntoVenta: string;
  numeroComprobante: string;
  proveedor: string;
  cuit: string;
  tipoIva: string;
  netoGravado: number;
  iva21: number;
  iva105: number;
  iva27: number;
  exento: number;
  perIibb: number;
  perIva: number;
  impInt: number;
  impMun: number;
  impNac: number;
  total: number;
  nroFormulario: number;
}

interface LibroIvaComprasTotales {
  netoGravado: number;
  iva21: number;
  iva105: number;
  iva27: number;
  exento: number;
  perIibb: number;
  perIva: number;
  impInt: number;
  impMun: number;
  impNac: number;
  total: number;
}

const LIBRO_IVA_TABLE_COL_COUNT = 18;

const totalesComprasInicial: LibroIvaComprasTotales = {
  netoGravado: 0,
  iva21: 0,
  iva105: 0,
  iva27: 0,
  exento: 0,
  perIibb: 0,
  perIva: 0,
  impInt: 0,
  impMun: 0,
  impNac: 0,
  total: 0
};

const sumLibroIvaComprasRows = (rows: LibroIvaComprasRow[]): LibroIvaComprasTotales =>
  rows.reduce(
    (acc, row) => ({
      netoGravado: acc.netoGravado + row.netoGravado,
      iva21: acc.iva21 + row.iva21,
      iva105: acc.iva105 + row.iva105,
      iva27: acc.iva27 + row.iva27,
      exento: acc.exento + row.exento,
      perIibb: acc.perIibb + row.perIibb,
      perIva: acc.perIva + row.perIva,
      impInt: acc.impInt + row.impInt,
      impMun: acc.impMun + row.impMun,
      impNac: acc.impNac + row.impNac,
      total: acc.total + row.total
    }),
    { ...totalesComprasInicial }
  );

const buildComprasFootRowPdf = (totales: LibroIvaComprasTotales): PdfFootCellDef[] => [
  pdfFootLabelCell('Totales', 7),
  pdfFootAmountCell(formatCurrencyPdfCompact(totales.netoGravado), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_COMPRAS),
  pdfFootAmountCell(formatCurrencyPdfCompact(totales.iva21), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_COMPRAS),
  pdfFootAmountCell(formatCurrencyPdfCompact(totales.iva105), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_COMPRAS),
  pdfFootAmountCell(formatCurrencyPdfCompact(totales.iva27), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_COMPRAS),
  pdfFootAmountCell(formatCurrencyPdfCompact(totales.exento), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_COMPRAS),
  pdfFootAmountCell(formatCurrencyPdfCompact(totales.perIibb), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_COMPRAS),
  pdfFootAmountCell(formatCurrencyPdfCompact(totales.perIva), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_COMPRAS),
  pdfFootAmountCell(formatCurrencyPdfCompact(totales.impInt), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_COMPRAS),
  pdfFootAmountCell(formatCurrencyPdfCompact(totales.impMun), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_COMPRAS),
  pdfFootAmountCell(formatCurrencyPdfCompact(totales.impNac), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_COMPRAS),
  pdfFootAmountCell(formatCurrencyPdfCompact(totales.total), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_COMPRAS)
];

// Función para formatear el tipo de IVA
const formatearTipoIva = (tipoIvaOriginal?: string): string => {
  const tipoIva = tipoIvaOriginal?.trim();
  if (!tipoIva) {
    return '-';
  }

  const normalized = tipoIva.toUpperCase();

  if (normalized === 'I' || normalized.includes('RESPONSABLE') || normalized.includes('INSCRIPTO')) {
    return 'Resp. Insc.';
  }

  if (normalized === 'M' || normalized.includes('MONOTRIBUTO')) {
    return 'Monotributo';
  }

  if (normalized === 'E' || normalized.includes('EXENTO')) {
    return 'Exento';
  }

  if (normalized === 'C' || normalized.includes('CONSUMIDOR') || normalized.includes('FINAL')) {
    return 'Cons. Final';
  }

  if (normalized === 'N' || normalized.includes('NO') || normalized.includes('CATEGORIZADO')) {
    return 'No Categorizado';
  }

  return tipoIva;
};

const TIPO_IVA_ORDEN = ['Resp. Insc.', 'Monotributo', 'Exento', 'Cons. Final', 'No Categorizado'];

const claveLetraComprobante = (item: LibroIvaComprasItem): string =>
  (item.letra ?? '').trim().toUpperCase() || '-';

const claveTipoIvaComprobante = (item: LibroIvaComprasItem): string => formatearTipoIva(item.tipoIva);

const claveProvinciaComprobante = (item: LibroIvaComprasItem): string => claveProvincia(item.codigoProvincia);

const nombresProvinciaDesdeItems = (items: LibroIvaComprasItem[]): Map<string, string> => {
  const nombres = new Map<string, string>();
  items.forEach((item) => {
    const clave = claveProvinciaComprobante(item);
    if (!nombres.has(clave)) nombres.set(clave, (item.nombreProvincia ?? '').trim() || SIN_PROVINCIA_LABEL);
  });
  return nombres;
};

const filtrarPorProvincias = (items: LibroIvaComprasItem[], filtro: string[]): LibroIvaComprasItem[] =>
  filtro.length ? items.filter((item) => provinciaEnFiltro(item.codigoProvincia, filtro)) : items;

/** Sin codigoProvincia/nombreProvincia en el DTO no se puede agrupar por provincia. */
const provinciaInformada = (items: LibroIvaComprasItem[]): boolean =>
  items.some((item) => item.codigoProvincia !== undefined || item.nombreProvincia !== undefined);

type SeleccionResumen = { provincia: string | null; tipoIva: string | null; letra: string | null };

const filtrarPorSeleccion = (items: LibroIvaComprasItem[], seleccion: SeleccionResumen): LibroIvaComprasItem[] => {
  const { provincia, tipoIva, letra } = seleccion;
  if (!provincia && !tipoIva && !letra) return items;
  return items.filter((item) => {
    if (provincia && claveProvinciaComprobante(item) !== provincia) return false;
    if (tipoIva && claveTipoIvaComprobante(item) !== tipoIva) return false;
    if (letra && claveLetraComprobante(item) !== letra) return false;
    return true;
  });
};

const agruparTotalesPor = (
  items: LibroIvaComprasItem[],
  keyOf: (item: LibroIvaComprasItem) => string
): LibroIvaResumenGrupo[] => {
  const grupos = new Map<string, LibroIvaResumenGrupo>();

  items.forEach((item) => {
    const clave = keyOf(item) || '-';
    const prev = grupos.get(clave) ?? {
      clave,
      cantidad: 0,
      netoGravado: 0,
      iva21: 0,
      iva105: 0,
      iva27: 0,
      exento: 0,
      total: 0
    };
    grupos.set(clave, {
      clave,
      cantidad: prev.cantidad + 1,
      netoGravado: prev.netoGravado + (item.netoGrav ?? 0),
      iva21: prev.iva21 + (item.iva21 ?? 0),
      iva105: prev.iva105 + (item.iva10 ?? 0),
      iva27: prev.iva27 + (item.iva27 ?? 0),
      exento: prev.exento + (item.exento ?? 0),
      total: prev.total + (item.importeTotal ?? 0)
    });
  });

  return Array.from(grupos.values());
};

const ordenarConClaveAlFinal = (claveAlFinal: string) => (a: LibroIvaResumenGrupo, b: LibroIvaResumenGrupo): number => {
  if (a.clave === claveAlFinal) return 1;
  if (b.clave === claveAlFinal) return -1;
  return a.clave.localeCompare(b.clave, 'es');
};

const ordenarTiposIva = (a: LibroIvaResumenGrupo, b: LibroIvaResumenGrupo): number => {
  const indexA = TIPO_IVA_ORDEN.indexOf(a.clave);
  const indexB = TIPO_IVA_ORDEN.indexOf(b.clave);
  if (indexA !== -1 && indexB !== -1) return indexA - indexB;
  if (indexA !== -1) return -1;
  if (indexB !== -1) return 1;
  return ordenarConClaveAlFinal('-')(a, b);
};

const resumenPorProvincia = (
  items: LibroIvaComprasItem[],
  nombres: Map<string, string>
): LibroIvaResumenGrupo[] =>
  agruparTotalesPor(items, claveProvinciaComprobante).sort((a, b) => {
    if (a.clave === SIN_PROVINCIA_KEY) return 1;
    if (b.clave === SIN_PROVINCIA_KEY) return -1;
    return (nombres.get(a.clave) ?? a.clave).localeCompare(nombres.get(b.clave) ?? b.clave, 'es');
  });

const resumenPorTipoIva = (items: LibroIvaComprasItem[]): LibroIvaResumenGrupo[] =>
  agruparTotalesPor(items, claveTipoIvaComprobante).sort(ordenarTiposIva);

const resumenPorLetra = (items: LibroIvaComprasItem[]): LibroIvaResumenGrupo[] =>
  agruparTotalesPor(items, claveLetraComprobante).sort(ordenarConClaveAlFinal('-'));

// Convertir items del API a filas del libro IVA (usa netoGrav, iva10, iva21, iva27, exento del DTO)
const convertToLibroIvaRows = (items: LibroIvaComprasItem[]): LibroIvaComprasRow[] => {
  return items.map(item => {
    const tipoComprobante =
      (item.documento ?? '').trim()
      || (item.nroTipoFormulario ?? '').trim()
      || '-';

    const netoGravado = item.netoGrav ?? 0;
    const iva21 = item.iva21 ?? 0;
    const iva105 = item.iva10 ?? 0;
    const iva27 = item.iva27 ?? 0;
    const exento = item.exento ?? 0;
    const perIibb = item.perIibb ?? 0;
    const perIva = item.perIva ?? 0;
    const impInt = item.impInt ?? 0;
    const impMun = item.impMun ?? 0;
    const impNac = item.impNac ?? 0;
    const total = item.importeTotal ?? 0;

    const puntoVentaFormateado = (item.nroSuc ?? 0).toString().padStart(5, '0');
    const numeroComprobanteFormateado = (item.nroComprobante ?? 0).toString().padStart(8, '0');
    const fechaEmisionStr = item.fechaemision ? String(item.fechaemision) : '';

    return {
      fechaEmision: fechaEmisionStr,
      tipoComprobante,
      puntoVenta: puntoVentaFormateado,
      numeroComprobante: numeroComprobanteFormateado,
      proveedor: item.nombre || '-',
      cuit: item.cuit || '-',
      tipoIva: formatearTipoIva(item.tipoIva),
      netoGravado,
      iva21,
      iva105,
      iva27,
      exento,
      perIibb,
      perIva,
      impInt,
      impMun,
      impNac,
      total,
      nroFormulario: item.nroformulario
    };
  });
};

const totalesDesdeItems = (items: LibroIvaComprasItem[]): LibroIvaComprasTotales =>
  sumLibroIvaComprasRows(convertToLibroIvaRows(items));

type LibroIvaComprasFiltros = {
  fechaDesde: string;
  fechaHasta: string;
  nroProveedor: string;
  nroSuc: string;
  nombreProveedor: string;
  idCompania: string;
};

const filtrosToParams = (filters: LibroIvaComprasFiltros) => ({
  fechaDesde: filters.fechaDesde || undefined,
  fechaHasta: filters.fechaHasta || undefined,
  nroProveedor: filters.nroProveedor ? parseInt(filters.nroProveedor) : undefined,
  nroSuc: filters.nroSuc ? parseInt(filters.nroSuc) : undefined,
  nombreProveedor: filters.nombreProveedor || undefined,
  idCompania: filters.idCompania || undefined,
});

const LIBRO_IVA_COMPRAS_PAGE_SIZE = 20; // Límite del backend
const LIBRO_IVA_COMPRAS_PAGINAS_EN_PARALELO = 10;

const fetchLibroIvaComprasCompleto = async (filters: LibroIvaComprasFiltros): Promise<LibroIvaComprasItem[]> => {
  const params = filtrosToParams(filters);
  const primera = await facturaCompraService.getLibroIvaCompras({
    pageNumber: 1,
    pageSize: LIBRO_IVA_COMPRAS_PAGE_SIZE,
    ...params,
  });
  const items = [...primera.items];

  for (let desde = 2; desde <= primera.totalPages; desde += LIBRO_IVA_COMPRAS_PAGINAS_EN_PARALELO) {
    const hasta = Math.min(desde + LIBRO_IVA_COMPRAS_PAGINAS_EN_PARALELO - 1, primera.totalPages);
    const respuestas = await Promise.all(
      Array.from({ length: hasta - desde + 1 }, (_, i) =>
        facturaCompraService.getLibroIvaCompras({
          pageNumber: desde + i,
          pageSize: LIBRO_IVA_COMPRAS_PAGE_SIZE,
          ...params,
        })
      )
    );
    respuestas.forEach((r) => items.push(...r.items));
  }

  return items;
};

type ResumenesAbiertos = { provincia: boolean; tipoIva: boolean; letra: boolean };

type ResumenAgrupado = {
  /** null cuando el backend no informa la provincia del proveedor. */
  porProvincia: LibroIvaResumenGrupo[] | null;
  porTipoIva: LibroIvaResumenGrupo[];
  porLetra: LibroIvaResumenGrupo[];
  nombresProvincia: Map<string, string>;
};

const LibroIvaCompras: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [facturas, setFacturas] = useState<LibroIvaComprasItem[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingTxt, setExportingTxt] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const previewPdfUrlRef = useRef<string | null>(null);
  previewPdfUrlRef.current = previewPdfUrl;
  const [totalesInforme, setTotalesInforme] = useState<LibroIvaComprasTotales>(totalesComprasInicial);
  const [totalesListado, setTotalesListado] = useState<LibroIvaComprasTotales>(totalesComprasInicial);
  const [companias, setCompanias] = useState<Compania[]>([]);
  const [provinciasOpciones, setProvinciasOpciones] = useState<{ value: string; label: string }[]>([]);
  const [provinciasFiltro, setProvinciasFiltro] = useState<string[]>([]);
  const [provinciaSeleccionada, setProvinciaSeleccionada] = useState<string | null>(null);
  const [tipoIvaSeleccionado, setTipoIvaSeleccionado] = useState<string | null>(null);
  const [letraSeleccionada, setLetraSeleccionada] = useState<string | null>(null);
  const libroCompletoCacheRef = useRef<{ key: string; promise: Promise<LibroIvaComprasItem[]> } | null>(null);
  const [resumenesAbiertos, setResumenesAbiertos] = useState<ResumenesAbiertos>({
    provincia: true,
    tipoIva: true,
    letra: true
  });
  const [resumenAgrupado, setResumenAgrupado] = useState<ResumenAgrupado | null>(null);
  const [loadingResumenAgrupado, setLoadingResumenAgrupado] = useState(false);
  const [errorResumenAgrupado, setErrorResumenAgrupado] = useState(false);
  const [intentoResumen, setIntentoResumen] = useState(0);

  // Inicializar fechas: primer día del mes actual y último día del mes actual
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  const [filters, setFilters] = useState<LibroIvaComprasFiltros>({
    fechaDesde: firstDayOfMonth.toISOString().split('T')[0],
    fechaHasta: lastDayOfMonth.toISOString().split('T')[0],
    nroProveedor: '',
    nroSuc: '',
    nombreProveedor: '',
    idCompania: readGlobalIdCompania() ?? '',
  });

  const resolveCompaniaParaInforme = useCallback(async (): Promise<Compania | null> => {
    const id = filters.idCompania?.trim();
    if (id) {
      try {
        return await companiaService.getCompania(id);
      } catch {
        return null;
      }
    }
    return fetchCompaniaParaPdf();
  }, [filters.idCompania]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await companiaService.getCompanias({ pageNumber: 1, pageSize: 200 });
        if (!cancelled) {
          setCompanias(
            (list || []).map((c) => ({
              ...c,
              idCompania: String(c.idCompania || '').trim(),
            })),
          );
        }
      } catch {
        if (!cancelled) setCompanias([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const provincias: Provincia[] = [];
        let pageNumber = 1;
        let totalPages = 1;
        do {
          const res = await provinciaService.getProvincias({ pageNumber, pageSize: 100 });
          provincias.push(...(res.items || []));
          totalPages = res.totalPages || 1;
          pageNumber++;
        } while (pageNumber <= totalPages && !cancelled);
        if (cancelled) return;
        const opciones = provincias
          .filter((p) => (p.nombreProvincia ?? '').trim())
          .map((p) => ({
            value: claveProvincia(p.codigoProvincia),
            label: (p.nombreProvincia ?? '').trim(),
          }))
          .sort((a, b) => a.label.localeCompare(b.label, 'es'));
        setProvinciasOpciones([...opciones, { value: SIN_PROVINCIA_KEY, label: SIN_PROVINCIA_LABEL }]);
      } catch {
        if (!cancelled) setProvinciasOpciones([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const obtenerLibroCompletoCacheado = useCallback((): Promise<LibroIvaComprasItem[]> => {
    const key = JSON.stringify(filters);
    if (libroCompletoCacheRef.current?.key !== key) {
      const promise = fetchLibroIvaComprasCompleto(filters);
      promise.catch(() => {
        if (libroCompletoCacheRef.current?.promise === promise) libroCompletoCacheRef.current = null;
      });
      libroCompletoCacheRef.current = { key, promise };
    }
    return libroCompletoCacheRef.current.promise;
  }, [filters]);

  const seleccionActiva =
    provinciaSeleccionada !== null || tipoIvaSeleccionado !== null || letraSeleccionada !== null;

  const filtroListadoActivo = provinciasFiltro.length > 0 || seleccionActiva;

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      if (filtroListadoActivo) {
        const libro = filtrarPorProvincias(await obtenerLibroCompletoCacheado(), provinciasFiltro);
        const listado = filtrarPorSeleccion(libro, {
          provincia: provinciaSeleccionada,
          tipoIva: tipoIvaSeleccionado,
          letra: letraSeleccionada
        });
        setFacturas(listado.slice(page * rowsPerPage, (page + 1) * rowsPerPage));
        setTotalCount(listado.length);
        setTotalesInforme(totalesDesdeItems(libro));
        setTotalesListado(totalesDesdeItems(listado));
        return;
      }

      const response = await facturaCompraService.getLibroIvaCompras({
        pageNumber: page + 1,
        pageSize: rowsPerPage,
        ...filtrosToParams(filters),
      });

      const totales: LibroIvaComprasTotales = {
        netoGravado: response.totalNetoGrav ?? 0,
        iva21: response.totalIva21 ?? 0,
        iva105: response.totalIva10 ?? 0,
        iva27: response.totalIva27 ?? 0,
        exento: response.totalExento ?? 0,
        perIibb: response.totalPerIibb ?? 0,
        perIva: response.totalPerIva ?? 0,
        impInt: response.totalImpInt ?? 0,
        impMun: response.totalImpMun ?? 0,
        impNac: response.totalImpNac ?? 0,
        total: response.totalImporteTotal ?? 0
      };
      setFacturas(response.items);
      setTotalCount(response.totalCount);
      setTotalesInforme(totales);
      setTotalesListado(totales);
    } catch (error) {
      console.error('Error al obtener el libro IVA compras:', error);
      enqueueSnackbar('No se pudieron cargar los datos del libro IVA.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [
    enqueueSnackbar,
    page,
    rowsPerPage,
    filters,
    filtroListadoActivo,
    provinciasFiltro,
    provinciaSeleccionada,
    tipoIvaSeleccionado,
    letraSeleccionada,
    obtenerLibroCompletoCacheado
  ]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const necesitaResumenAgrupado = resumenesAbiertos.provincia || resumenesAbiertos.tipoIva || resumenesAbiertos.letra;

  useEffect(() => {
    if (!necesitaResumenAgrupado) return;
    let cancelled = false;
    setLoadingResumenAgrupado(true);
    setErrorResumenAgrupado(false);
    (async () => {
      try {
        const libroCompleto = await obtenerLibroCompletoCacheado();
        if (cancelled) return;
        const conProvincia = provinciaInformada(libroCompleto);
        const libro = conProvincia ? filtrarPorProvincias(libroCompleto, provinciasFiltro) : libroCompleto;
        const base = conProvincia
          ? filtrarPorSeleccion(libro, { provincia: provinciaSeleccionada, tipoIva: null, letra: null })
          : libro;
        const nombresProvincia = nombresProvinciaDesdeItems(libroCompleto);
        setResumenAgrupado({
          porProvincia: conProvincia ? resumenPorProvincia(libro, nombresProvincia) : null,
          porTipoIva: resumenPorTipoIva(base),
          porLetra: resumenPorLetra(base),
          nombresProvincia
        });
      } catch (error) {
        console.error('Error al calcular resúmenes del libro IVA compras:', error);
        if (!cancelled) {
          setResumenAgrupado(null);
          setErrorResumenAgrupado(true);
          enqueueSnackbar('No se pudieron calcular los resúmenes por Provincia, Tipo IVA y Letra.', { variant: 'error' });
        }
      } finally {
        if (!cancelled) setLoadingResumenAgrupado(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    necesitaResumenAgrupado,
    obtenerLibroCompletoCacheado,
    provinciasFiltro,
    provinciaSeleccionada,
    enqueueSnackbar,
    intentoResumen
  ]);

  const reintentarResumen = () => setIntentoResumen((n) => n + 1);

  const toggleResumen = (clave: keyof ResumenesAbiertos) => (expanded: boolean) =>
    setResumenesAbiertos((prev) => ({ ...prev, [clave]: expanded }));

  useEffect(() => {
    return () => {
      if (previewPdfUrlRef.current) URL.revokeObjectURL(previewPdfUrlRef.current);
    };
  }, []);

  const handleClosePdfPreview = useCallback(() => {
    setPreviewPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const limpiarSeleccionResumenes = () => {
    setProvinciaSeleccionada(null);
    setTipoIvaSeleccionado(null);
    setLetraSeleccionada(null);
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const name = event.target.name || '';
    const value = event.target.value;
    setFilters({ ...filters, [name]: value });
    limpiarSeleccionResumenes();
    setPage(0);
  };

  const handleToggleProvinciaSeleccionada = (clave: string) => {
    setProvinciaSeleccionada((prev) => (prev === clave ? null : clave));
    setPage(0);
  };

  const handleToggleTipoIvaSeleccionado = (clave: string) => {
    setTipoIvaSeleccionado((prev) => (prev === clave ? null : clave));
    setPage(0);
  };

  const handleToggleLetraSeleccionada = (clave: string) => {
    setLetraSeleccionada((prev) => (prev === clave ? null : clave));
    setPage(0);
  };

  const handleProvinciasFiltroChange = (value: string | string[]) => {
    const seleccion = typeof value === 'string' ? value.split(',') : value;
    setProvinciasFiltro(seleccion);
    setProvinciaSeleccionada((prev) => (prev && seleccion.length > 0 && !seleccion.includes(prev) ? null : prev));
    setPage(0);
  };

  const nombreProvinciaPorClave = (clave: string): string =>
    provinciasOpciones.find((o) => o.value === clave)?.label
    ?? resumenAgrupado?.nombresProvincia.get(clave)
    ?? clave;

  const descripcionSeleccionResumenes = (): string =>
    [
      provinciasFiltro.length ? `Provincias: ${provinciasFiltro.map(nombreProvinciaPorClave).join(', ')}` : '',
      provinciaSeleccionada ? `Provincia: ${nombreProvinciaPorClave(provinciaSeleccionada)}` : '',
      tipoIvaSeleccionado ? `Tipo IVA: ${tipoIvaSeleccionado}` : '',
      letraSeleccionada ? `Letra: ${letraSeleccionada}` : ''
    ].filter(Boolean).join(' — ');

  /** Libro completo con el filtro de provincias y la provincia, tipo IVA y letra elegidos en los resúmenes. */
  const obtenerListadoCompleto = async (): Promise<LibroIvaComprasItem[]> =>
    filtrarPorSeleccion(filtrarPorProvincias(await obtenerLibroCompletoCacheado(), provinciasFiltro), {
      provincia: provinciaSeleccionada,
      tipoIva: tipoIvaSeleccionado,
      letra: letraSeleccionada
    });

  const handleExportExcel = async () => {
    if (totalCount === 0) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
      return;
    }

    try {
      setExporting(true);
      const todasLasFacturas = await obtenerListadoCompleto();

      if (!todasLasFacturas.length) {
        enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
        return;
      }

      const rows = convertToLibroIvaRows(todasLasFacturas);
      const totalesExport = sumLibroIvaComprasRows(rows);

      const data = rows.map((row) => ({
        'Fecha': row.fechaEmision ? formatearFecha(row.fechaEmision) : '',
        'Tipo': row.tipoComprobante,
        'Punto Venta': row.puntoVenta,
        'Número': row.numeroComprobante,
        'Proveedor': row.proveedor,
        'CUIT': row.cuit,
        'Tipo IVA': row.tipoIva,
        'Neto Gravado': row.netoGravado,
        'IVA 21%': row.iva21,
        'IVA 10.5%': row.iva105,
        'IVA 27%': row.iva27,
        'Exento': row.exento,
        'Perc.IIBB': row.perIibb,
        'Perc.IVA': row.perIva,
        'Imp.Int.': row.impInt,
        'Imp.Mun.': row.impMun,
        'Imp.Nac.': row.impNac,
        'Importe total': row.total
      }));

      data.push({
        'Fecha': 'Totales',
        'Tipo': '',
        'Punto Venta': '',
        'Número': '',
        'Proveedor': '',
        'CUIT': '',
        'Tipo IVA': '',
        'Neto Gravado': totalesExport.netoGravado,
        'IVA 21%': totalesExport.iva21,
        'IVA 10.5%': totalesExport.iva105,
        'IVA 27%': totalesExport.iva27,
        'Exento': totalesExport.exento,
        'Perc.IIBB': totalesExport.perIibb,
        'Perc.IVA': totalesExport.perIva,
        'Imp.Int.': totalesExport.impInt,
        'Imp.Mun.': totalesExport.impMun,
        'Imp.Nac.': totalesExport.impNac,
        'Importe total': totalesExport.total
      });

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Libro IVA Compras');

      const today = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `libro_iva_compras_${today}.xlsx`);
      enqueueSnackbar('Exportación completada.', { variant: 'success' });
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      enqueueSnackbar('No se pudo exportar a Excel.', { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const parseInputDate = (value: string): Date | null => {
    if (!value) return null;
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };

  const downloadTextFile = (content: string, fileName: string) => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportTxtArca = async () => {
    const fechaDesde = parseInputDate(filters.fechaDesde);
    const fechaHasta = parseInputDate(filters.fechaHasta);

    if (!fechaDesde || !fechaHasta) {
      enqueueSnackbar('Debe seleccionar fecha desde y fecha hasta para exportar TXT.', { variant: 'warning' });
      return;
    }

    if (fechaDesde > fechaHasta) {
      enqueueSnackbar('La fecha desde no puede ser mayor que la fecha hasta.', { variant: 'warning' });
      return;
    }

    try {
      setExportingTxt(true);
      const result = await facturaCompraService.exportLibroIvaComprasArcaTxt({
        fechaDesde,
        fechaHasta,
        idCompania: filters.idCompania || undefined,
      });

      downloadTextFile(result.comprobantesContent, result.comprobantesFileName);
      downloadTextFile(result.alicuotasContent, result.alicuotasFileName);
      enqueueSnackbar('Archivos TXT generados para ARCA.', { variant: 'success' });
    } catch (error) {
      console.error('Error al exportar TXT ARCA:', error);
      enqueueSnackbar('No se pudieron generar los archivos TXT para ARCA.', { variant: 'error' });
    } finally {
      setExportingTxt(false);
    }
  };

  const handlePreviewPdfLibroIva = async () => {
    if (totalCount === 0) {
      enqueueSnackbar('No hay información para exportar.', { variant: 'warning' });
      return;
    }

    try {
      setExportingPdf(true);
      const todasLasFacturas = await obtenerListadoCompleto();

      if (!todasLasFacturas.length) {
        enqueueSnackbar('No hay información para exportar.', { variant: 'warning' });
        return;
      }

      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const marginX = 40;
      const pageW = doc.internal.pageSize.getWidth();
      const headerTop = 32;
      const logoMaxW = 76;
      const logoMaxH = 42;
      const rightColW = 260;
      const rightX = pageW - marginX;

      const companiaSeleccionada = Boolean(filters.idCompania?.trim());
      const compania = await resolveCompaniaParaInforme();
      const logoPng =
        companiaSeleccionada && compania?.idCompania
          ? await fetchLogoPngDataUrl(compania.idCompania)
          : null;

      if (logoPng) {
        doc.addImage(logoPng, 'PNG', marginX, headerTop, logoMaxW, logoMaxH, undefined, 'FAST');
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(15);
      doc.setTextColor(25, 55, 95);
      doc.text('Libro IVA Compras', pageW / 2, headerTop + 26, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);

      doc.setFontSize(7);
      const datosComp =
        companiaSeleccionada && compania ? lineasDatosCompania(compania) : [];
      // Sin maxWidth en align:right: jsPDF justifica y abre huecos enormes entre palabras.
      let ry = headerTop + 4;
      datosComp.forEach((linea) => {
        const wrapped = doc.splitTextToSize(linea, rightColW);
        wrapped.forEach((sub: string) => {
          doc.text(sub, rightX, ry, { align: 'right' });
          ry += 8;
        });
      });

      const headerBottom = Math.max(
        logoPng ? headerTop + logoMaxH + 4 : headerTop,
        ry + 4,
        headerTop + 48,
      );
      let currentY = headerBottom;

      doc.setFontSize(8);
      const generadoEn = new Date();
      doc.text(`Generado: ${generadoEn.toLocaleString('es-AR')}`, marginX, currentY);
      currentY += 12;

      if (filters.fechaDesde && filters.fechaHasta) {
        const fechaDesdeParts = filters.fechaDesde.split('-');
        const fechaHastaParts = filters.fechaHasta.split('-');
        const fechaDesdeLocal = new Date(
          parseInt(fechaDesdeParts[0], 10),
          parseInt(fechaDesdeParts[1], 10) - 1,
          parseInt(fechaDesdeParts[2], 10)
        );
        const fechaHastaLocal = new Date(
          parseInt(fechaHastaParts[0], 10),
          parseInt(fechaHastaParts[1], 10) - 1,
          parseInt(fechaHastaParts[2], 10)
        );
        doc.text(
          `Aplicación IVA: ${formatearFecha(fechaDesdeLocal)} - ${formatearFecha(fechaHastaLocal)}`,
          marginX,
          currentY
        );
        currentY += 14;
      }

      const seleccion = descripcionSeleccionResumenes();
      if (seleccion) {
        doc.text(`Filtro: ${seleccion}`, marginX, currentY);
        currentY += 14;
      }

      const rows = convertToLibroIvaRows(todasLasFacturas);
      const totalesExport = sumLibroIvaComprasRows(rows);

      const autoTable = (await import('jspdf-autotable')).default;
      const tableWidth = pdfLibroIvaTableWidth(pageW, marginX);
      const comprasTableStyles = pdfLibroIvaComprasTableStyles();

      autoTable(doc, {
        startY: currentY,
        tableWidth,
        head: [Array.from(PDF_LIBRO_IVA_COMPRAS_HEAD)],
        body: rows.map((row) => [
          row.fechaEmision ? formatearFecha(row.fechaEmision) : '-',
          row.tipoComprobante,
          row.puntoVenta,
          row.numeroComprobante,
          row.proveedor,
          row.cuit,
          row.tipoIva,
          formatCurrencyPdfCompact(row.netoGravado),
          formatCurrencyPdfCompact(row.iva21),
          formatCurrencyPdfCompact(row.iva105),
          formatCurrencyPdfCompact(row.iva27),
          formatCurrencyPdfCompact(row.exento),
          formatCurrencyPdfCompact(row.perIibb),
          formatCurrencyPdfCompact(row.perIva),
          formatCurrencyPdfCompact(row.impInt),
          formatCurrencyPdfCompact(row.impMun),
          formatCurrencyPdfCompact(row.impNac),
          formatCurrencyPdfCompact(row.total)
        ]),
        foot: [buildComprasFootRowPdf(totalesExport)],
        showFoot: 'lastPage',
        margin: { left: marginX, right: marginX },
        ...comprasTableStyles,
        footStyles: pdfLibroIvaFootStyles(PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_COMPRAS),
        columnStyles: pdfLibroIvaComprasColumnStyles(tableWidth)
      });

      const pdfBlob = doc.output('blob');
      const safeBlob =
        pdfBlob instanceof Blob && pdfBlob.type === 'application/pdf'
          ? pdfBlob
          : new Blob([pdfBlob], { type: 'application/pdf' });
      const url = URL.createObjectURL(safeBlob);
      setPreviewPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
      enqueueSnackbar('Vista previa del PDF lista.', { variant: 'success' });
    } catch (error: unknown) {
      if ((error as { name?: string })?.name === 'AbortError') {
        enqueueSnackbar('Exportación cancelada por el usuario.', { variant: 'warning' });
      } else {
        console.error('Error al generar el PDF:', error);
        enqueueSnackbar('No se pudo generar el PDF.', { variant: 'error' });
      }
    } finally {
      setExportingPdf(false);
    }
  };

  const renderFooter = () => {
    if (loading || facturas.length === 0) {
      return null;
    }

    return (
      <TableRow>
        <TableCell colSpan={7} sx={footerCellSx}>
          Totales
        </TableCell>
        <TableCell align="right" sx={footerCellSx}>
          {formatCurrency(totalesListado.netoGravado)}
        </TableCell>
        <TableCell align="right" sx={footerCellSx}>
          {formatCurrency(totalesListado.iva21)}
        </TableCell>
        <TableCell align="right" sx={{ ...footerCellSx, display: { xs: 'none', lg: 'table-cell' } }}>
          {formatCurrency(totalesListado.iva105)}
        </TableCell>
        <TableCell align="right" sx={{ ...footerCellSx, display: { xs: 'none', lg: 'table-cell' } }}>
          {formatCurrency(totalesListado.iva27)}
        </TableCell>
        <TableCell align="right" sx={{ ...footerCellSx, display: { xs: 'none', lg: 'table-cell' } }}>
          {formatCurrency(totalesListado.exento)}
        </TableCell>
        <TableCell align="right" sx={{ ...footerCellSx, display: { xs: 'none', xl: 'table-cell' } }}>
          {formatCurrency(totalesListado.perIibb)}
        </TableCell>
        <TableCell align="right" sx={{ ...footerCellSx, display: { xs: 'none', xl: 'table-cell' } }}>
          {formatCurrency(totalesListado.perIva)}
        </TableCell>
        <TableCell align="right" sx={{ ...footerCellSx, display: { xs: 'none', xl: 'table-cell' } }}>
          {formatCurrency(totalesListado.impInt)}
        </TableCell>
        <TableCell align="right" sx={{ ...footerCellSx, display: { xs: 'none', xl: 'table-cell' } }}>
          {formatCurrency(totalesListado.impMun)}
        </TableCell>
        <TableCell align="right" sx={{ ...footerCellSx, display: { xs: 'none', xl: 'table-cell' } }}>
          {formatCurrency(totalesListado.impNac)}
        </TableCell>
        <TableCell align="right" sx={footerCellSx}>
          {formatCurrency(totalesListado.total)}
        </TableCell>
      </TableRow>
    );
  };

  const renderBody = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={LIBRO_IVA_TABLE_COL_COUNT} align="center">
            <CircularProgress size={24} />
          </TableCell>
        </TableRow>
      );
    }

    if (!loading && facturas.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={LIBRO_IVA_TABLE_COL_COUNT} align="center">
            No hay facturas para el período seleccionado.
          </TableCell>
        </TableRow>
      );
    }

    const rows = convertToLibroIvaRows(facturas);

    return (
      <>
        {rows.map((row, index) => (
          <TableRow key={`${row.nroFormulario}-${index}`} hover>
            <TableCell>
              {row.fechaEmision ? formatearFecha(row.fechaEmision) : '-'}
            </TableCell>
            <TableCell>{row.tipoComprobante}</TableCell>
            <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
              {row.puntoVenta}
            </TableCell>
            <TableCell align="center">{row.numeroComprobante}</TableCell>
            <TableCell>{row.proveedor}</TableCell>
            <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{row.cuit}</TableCell>
            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{row.tipoIva}</TableCell>
            <TableCell align="right">{formatCurrency(row.netoGravado)}</TableCell>
            <TableCell align="right">{formatCurrency(row.iva21)}</TableCell>
            <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
              {formatCurrency(row.iva105)}
            </TableCell>
            <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
              {formatCurrency(row.iva27)}
            </TableCell>
            <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
              {formatCurrency(row.exento)}
            </TableCell>
            <TableCell align="right" sx={{ display: { xs: 'none', xl: 'table-cell' } }}>
              {formatCurrency(row.perIibb)}
            </TableCell>
            <TableCell align="right" sx={{ display: { xs: 'none', xl: 'table-cell' } }}>
              {formatCurrency(row.perIva)}
            </TableCell>
            <TableCell align="right" sx={{ display: { xs: 'none', xl: 'table-cell' } }}>
              {formatCurrency(row.impInt)}
            </TableCell>
            <TableCell align="right" sx={{ display: { xs: 'none', xl: 'table-cell' } }}>
              {formatCurrency(row.impMun)}
            </TableCell>
            <TableCell align="right" sx={{ display: { xs: 'none', xl: 'table-cell' } }}>
              {formatCurrency(row.impNac)}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>
              {formatCurrency(row.total)}
            </TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  const renderCards = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress size={40} />
        </Box>
      );
    }

    if (!loading && facturas.length === 0) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <Typography variant="body1" color="text.secondary">
            No hay facturas para el período seleccionado.
          </Typography>
        </Box>
      );
    }

    const rows = convertToLibroIvaRows(facturas);

    return (
      <Stack spacing={2}>
        {rows.map((row, index) => (
          <Card
            key={`${row.nroFormulario}-${index}`}
            sx={{
              borderRadius: 2,
              border: '1px solid #e0e0e0',
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              },
            }}
          >
            <CardContent>
              <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                      {row.tipoComprobante}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {row.fechaEmision ? formatearFecha(row.fechaEmision) : '-'}
                    </Typography>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    {formatCurrency(row.total)}
                  </Typography>
                </Stack>

                <Divider />

                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      P.Vta / Número:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {row.puntoVenta} - {row.numeroComprobante}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Proveedor:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}>
                      {row.proveedor}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      CUIT:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {row.cuit}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Tipo IVA:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {row.tipoIva}
                    </Typography>
                  </Stack>
                </Stack>

                <Divider />

                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Neto Gravado:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatCurrency(row.netoGravado)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      IVA 21%:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatCurrency(row.iva21)}
                    </Typography>
                  </Stack>
                  {row.iva105 !== 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        IVA 10.5%:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatCurrency(row.iva105)}
                      </Typography>
                    </Stack>
                  )}
                  {row.iva27 !== 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        IVA 27%:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatCurrency(row.iva27)}
                      </Typography>
                    </Stack>
                  )}
                  {row.exento !== 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Exento:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatCurrency(row.exento)}
                      </Typography>
                    </Stack>
                  )}
                  {row.perIibb !== 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Perc.IIBB:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatCurrency(row.perIibb)}</Typography>
                    </Stack>
                  )}
                  {row.perIva !== 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Perc.IVA:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatCurrency(row.perIva)}</Typography>
                    </Stack>
                  )}
                  {row.impInt !== 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Imp.Int.:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatCurrency(row.impInt)}</Typography>
                    </Stack>
                  )}
                  {row.impMun !== 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Imp.Mun.:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatCurrency(row.impMun)}</Typography>
                    </Stack>
                  )}
                  {row.impNac !== 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Imp.Nac.:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatCurrency(row.impNac)}</Typography>
                    </Stack>
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    );
  };

  const renderMobileTotales = () => {
    if (loading || facturas.length === 0) {
      return null;
    }

    return (
      <Card sx={{ mt: 2, borderRadius: 2, border: '2px solid #bdbdbd', bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
            Totales del informe
          </Typography>
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">Neto Gravado</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(totalesListado.netoGravado)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">IVA 21%</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(totalesListado.iva21)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">IVA 10.5%</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(totalesListado.iva105)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">IVA 27%</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(totalesListado.iva27)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">Exento</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(totalesListado.exento)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">Perc.IIBB</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(totalesListado.perIibb)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">Perc.IVA</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(totalesListado.perIva)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">Imp.Int.</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(totalesListado.impInt)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">Imp.Mun.</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(totalesListado.impMun)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">Imp.Nac.</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(totalesListado.impNac)}</Typography>
            </Stack>
            <Divider />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body1" sx={{ fontWeight: 700 }}>Importe total</Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, color: '#1976d2' }}>{formatCurrency(totalesListado.total)}</Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const kpisResumen: KpiBarItem[] = [
    {
      key: 'comprobantes',
      icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 20 }} />,
      iconColor: '#757575',
      value: String(totalCount),
      label: 'comprobantes',
    },
    {
      key: 'neto',
      icon: <PriceCheckIcon sx={{ fontSize: 20 }} />,
      iconColor: '#00897b',
      value: formatCurrency(totalesInforme.netoGravado),
      valueColor: '#00897b',
      label: 'neto gravado',
    },
    {
      key: 'iva21',
      icon: <PercentIcon sx={{ fontSize: 20 }} />,
      iconColor: '#1976d2',
      value: formatCurrency(totalesInforme.iva21),
      valueColor: '#1976d2',
      label: 'IVA 21%',
    },
    {
      key: 'iva105',
      icon: <PercentIcon sx={{ fontSize: 20 }} />,
      iconColor: '#7b1fa2',
      value: formatCurrency(totalesInforme.iva105),
      valueColor: '#7b1fa2',
      label: 'IVA 10.5%',
    },
    {
      key: 'total',
      icon: <AttachMoneyIcon sx={{ fontSize: 20 }} />,
      iconColor: '#2e7d32',
      value: formatCurrency(totalesInforme.total),
      valueColor: '#2e7d32',
      label: 'total',
    },
  ];

  const chipSeleccion = (clave: string | null, onDelete: (clave: string) => void, etiqueta = clave) =>
    clave ? (
      <Chip size="small" color="primary" label={`Listado: ${etiqueta}`} onDelete={() => onDelete(clave)} />
    ) : null;

  const provinciaNoInformada = resumenAgrupado !== null && resumenAgrupado.porProvincia === null;

  const renderResumenes = () => {
    if (!loading && totalCount === 0 && !filtroListadoActivo) {
      return null;
    }

    return (
      <Box sx={{ mt: 3 }}>
        <LibroIvaResumenAccordion
          titulo="Subtotales por provincia"
          descripcion="Hacé clic en una provincia para ver solo sus comprobantes en el listado; volvé a hacer clic para ver todas."
          icon={<PlaceOutlinedIcon color="primary" fontSize="small" />}
          expanded={resumenesAbiertos.provincia}
          onChange={toggleResumen('provincia')}
          accion={chipSeleccion(
            provinciaSeleccionada,
            handleToggleProvinciaSeleccionada,
            provinciaSeleccionada ? nombreProvinciaPorClave(provinciaSeleccionada) : null
          )}
        >
          {provinciaNoInformada && !loadingResumenAgrupado ? (
            <Alert severity="info">
              El servidor no informó la provincia de los proveedores para esta consulta.
            </Alert>
          ) : (
            <LibroIvaResumenAgrupadoTable
              columnaClave="Provincia"
              etiquetaClave={nombreProvinciaPorClave}
              grupos={resumenAgrupado?.porProvincia ?? null}
              loading={loadingResumenAgrupado}
              error={errorResumenAgrupado}
              onReintentar={reintentarResumen}
              claveSeleccionada={provinciaSeleccionada}
              onToggleClave={handleToggleProvinciaSeleccionada}
              etiquetaIva21="IVA 21%"
              etiquetaIva105="IVA 10.5%"
            />
          )}
        </LibroIvaResumenAccordion>

        <LibroIvaResumenAccordion
          titulo="Resumen por Tipo IVA"
          descripcion="Hacé clic en una categoría para ver solo sus comprobantes en el listado; volvé a hacer clic para ver todas."
          icon={<BadgeOutlinedIcon color="primary" fontSize="small" />}
          expanded={resumenesAbiertos.tipoIva}
          onChange={toggleResumen('tipoIva')}
          accion={chipSeleccion(tipoIvaSeleccionado, handleToggleTipoIvaSeleccionado)}
        >
          <LibroIvaResumenAgrupadoTable
            columnaClave="Tipo IVA"
            grupos={resumenAgrupado?.porTipoIva ?? null}
            loading={loadingResumenAgrupado}
            error={errorResumenAgrupado}
            onReintentar={reintentarResumen}
            claveSeleccionada={tipoIvaSeleccionado}
            onToggleClave={handleToggleTipoIvaSeleccionado}
            etiquetaIva21="IVA 21%"
            etiquetaIva105="IVA 10.5%"
          />
        </LibroIvaResumenAccordion>

        <LibroIvaResumenAccordion
          titulo="Resumen por Letra"
          descripcion="Hacé clic en una letra para ver solo sus comprobantes en el listado; volvé a hacer clic para ver todas."
          icon={<SortByAlphaIcon color="primary" fontSize="small" />}
          expanded={resumenesAbiertos.letra}
          onChange={toggleResumen('letra')}
          accion={chipSeleccion(letraSeleccionada, handleToggleLetraSeleccionada)}
        >
          <LibroIvaResumenAgrupadoTable
            columnaClave="Letra"
            grupos={resumenAgrupado?.porLetra ?? null}
            loading={loadingResumenAgrupado}
            error={errorResumenAgrupado}
            onReintentar={reintentarResumen}
            claveSeleccionada={letraSeleccionada}
            onToggleClave={handleToggleLetraSeleccionada}
            etiquetaIva21="IVA 21%"
            etiquetaIva105="IVA 10.5%"
          />
        </LibroIvaResumenAccordion>
      </Box>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/compras/informes')}
                sx={{ textTransform: 'none' }}
              >
                Informes
              </Button>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 600,
                  color: '#1976d2'
                }}
              >
                Libro IVA Compras
              </Typography>
            </Stack>
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={1}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              <Button
                variant="contained"
                color="primary"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportExcel}
                disabled={loading || exporting}
                sx={{
                  borderRadius: 2,
                  px: { xs: 2, sm: 3 },
                  py: 1,
                  width: { xs: '100%', sm: 'auto' },
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(245, 158, 11, 0.4)',
                  }
                }}
              >
                Exportar Excel
              </Button>
              <Tooltip title="Genera los archivos TXT de comprobantes y alícuotas para la presentación del Libro IVA Digital Compras ante ARCA.">
                <Box component="span" sx={{ width: { xs: '100%', sm: 'auto' } }}>
                  <Button
                    variant="outlined"
                    startIcon={<ArticleIcon />}
                    onClick={() => void handleExportTxtArca()}
                    disabled={loading || exportingTxt}
                    sx={{
                      borderRadius: 2,
                      px: { xs: 2, sm: 3 },
                      py: 1,
                      width: { xs: '100%', sm: 'auto' },
                      textTransform: 'none',
                      fontWeight: 500,
                    }}
                  >
                    {exportingTxt ? 'Generando TXT…' : 'Exportar TXT'}
                  </Button>
                </Box>
              </Tooltip>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => void handlePreviewPdfLibroIva()}
                disabled={loading || exportingPdf}
                sx={{
                  borderRadius: 2,
                  px: { xs: 2, sm: 3 },
                  py: 1,
                  width: { xs: '100%', sm: 'auto' },
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                {exportingPdf ? 'Generando PDF…' : 'Imprimir PDF'}
              </Button>
            </Stack>
          </Stack>

          <Card sx={{ mb: 3, bgcolor: '#fafafa' }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <SearchableSelect
                    label="Compañía"
                    allowEmpty
                    emptyOptionLabel="Todas las compañías"
                    value={filters.idCompania || null}
                    onChange={(v) => {
                      const id = v ?? '';
                      if (id) persistGlobalIdCompania(id);
                      setFilters((prev) => ({ ...prev, idCompania: id }));
                      limpiarSeleccionResumenes();
                      setPage(0);
                    }}
                    options={companias.map((c) => ({
                      value: c.idCompania,
                      label: `${c.idCompania} - ${c.nombre}`,
                    }))}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Fecha Aplicacion IVA desde"
                    type="date"
                    name="fechaDesde"
                    value={filters.fechaDesde}
                    onChange={handleFilterChange}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Fecha Aplicacion IVA hasta"
                    type="date"
                    name="fechaHasta"
                    value={filters.fechaHasta}
                    onChange={handleFilterChange}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Nro. Proveedor"
                    name="nroProveedor"
                    value={filters.nroProveedor}
                    onChange={handleFilterChange}
                    size="small"
                    type="number"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Nro. Sucursal"
                    name="nroSuc"
                    value={filters.nroSuc}
                    onChange={handleFilterChange}
                    size="small"
                    type="number"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Nombre Proveedor"
                    name="nombreProveedor"
                    value={filters.nombreProveedor}
                    onChange={handleFilterChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Tooltip
                    title={provinciaNoInformada ? 'El servidor no informa la provincia de los proveedores.' : ''}
                  >
                    <FormControl fullWidth size="small" disabled={provinciaNoInformada}>
                      <InputLabel id="libro-iva-compras-provincias-label" shrink>
                        Provincias
                      </InputLabel>
                      <Select
                        labelId="libro-iva-compras-provincias-label"
                        multiple
                        displayEmpty
                        value={provinciasFiltro}
                        onChange={(e) => handleProvinciasFiltroChange(e.target.value)}
                        input={<OutlinedInput notched label="Provincias" />}
                        renderValue={(seleccion) => {
                          if (seleccion.length === 0) return 'Todas';
                          if (seleccion.length <= 2) return seleccion.map(nombreProvinciaPorClave).join(', ');
                          return `${seleccion.length} provincias`;
                        }}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
                      >
                        {provinciasOpciones.map((opcion) => (
                          <MenuItem key={opcion.value} value={opcion.value} dense>
                            <Checkbox size="small" checked={provinciasFiltro.includes(opcion.value)} />
                            <ListItemText primary={opcion.label} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Tooltip>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <KpisBar items={kpisResumen} ariaLabel="Resumen impositivo del período" />

          {isMobile ? (
            <Box>
              {renderCards()}
              {renderMobileTotales()}
              <TablePagination
                rowsPerPageOptions={[10, 20, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                sx={{
                  mt: 2,
                  '& .MuiTablePagination-toolbar': {
                    flexWrap: 'wrap',
                    gap: 1,
                    px: 1
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: '0.75rem'
                  }
                }}
              />
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
                overflowX: 'auto',
                maxWidth: '100%',
                '& .MuiTableCell-root': {
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' }
                }
              }}
            >
              <Table size="small" sx={{ minWidth: 1680 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600, minWidth: { xs: 80, sm: 100 } }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: { xs: 100, sm: 120 } }}>Tipo</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'center', minWidth: { xs: 60, sm: 80 }, display: { xs: 'none', md: 'table-cell' } }}>P.Vta</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'center', minWidth: { xs: 80, sm: 100 } }}>Número</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: { xs: 150, sm: 200 } }}>Proveedor</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: { xs: 100, sm: 120 }, display: { xs: 'none', lg: 'table-cell' } }}>CUIT</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: { xs: 100, sm: 120 }, display: { xs: 'none', md: 'table-cell' } }}>Tipo IVA</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: { xs: 100, sm: 120 } }}>Neto Gravado</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: { xs: 80, sm: 100 } }}>IVA 21%</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: { xs: 80, sm: 100 }, display: { xs: 'none', lg: 'table-cell' } }}>IVA 10.5%</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: { xs: 80, sm: 100 }, display: { xs: 'none', lg: 'table-cell' } }}>IVA 27%</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: { xs: 80, sm: 100 }, display: { xs: 'none', lg: 'table-cell' } }}>Exento</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: 88, display: { xs: 'none', xl: 'table-cell' } }}>Perc.IIBB</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: 88, display: { xs: 'none', xl: 'table-cell' } }}>Perc.IVA</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: 88, display: { xs: 'none', xl: 'table-cell' } }}>Imp.Int.</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: 88, display: { xs: 'none', xl: 'table-cell' } }}>Imp.Mun.</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: 88, display: { xs: 'none', xl: 'table-cell' } }}>Imp.Nac.</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: { xs: 100, sm: 120 } }}>Importe total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {renderBody()}
                  {renderFooter()}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[10, 20, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                sx={{
                  borderTop: '1px solid #e0e0e0',
                  '& .MuiTablePagination-toolbar': {
                    flexWrap: 'wrap',
                    gap: 1,
                    px: { xs: 1, sm: 2 }
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }
                }}
              />
            </TableContainer>
          )}

          {renderResumenes()}
        </CardContent>
      </Card>

      {previewPdfUrl ? (
        <PDFViewer
          pdfUrl={previewPdfUrl}
          onClose={handleClosePdfPreview}
          title="Libro IVA Compras"
          documentId="libro-iva-compras-preview"
          documentType="informeCliente"
          downloadFileName={`libro_iva_compras_${new Date().toISOString().slice(0, 10)}.pdf`}
        />
      ) : null}
    </Box>
  );
};

export default LibroIvaCompras;

